import {
  useCallback,
  useRef,
  type ComponentPropsWithoutRef,
  type JSX,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cx } from './internal/cx.js';
import { useFocusTrap } from './internal/useFocusTrap.js';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Accessible label when no visible title is provided. */
  'aria-label'?: string;
  /** ID of the element that labels the dialog. */
  'aria-labelledby'?: string;
  className?: string;
}

export interface ModalFooterProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
}

function ModalFooter({ children, className, ...props }: ModalFooterProps): JSX.Element {
  return (
    <div className={cx('nrx-modal__footer', className)} {...props}>
      {children}
    </div>
  );
}

/** Accessible modal dialog with overlay, escape-to-close, focus trap, and scroll lock. */
export function Modal({
  open,
  onClose,
  children,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  className,
}: ModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleEscape = useCallback((): void => {
    onCloseRef.current();
  }, []);

  useFocusTrap(dialogRef, {
    active: open,
    onEscape: handleEscape,
    restoreFocus: true,
    lockScroll: true,
  });

  if (!open) return null;

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="nrx-modal__overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        className={cx('nrx-modal__content', className)}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

Modal.Footer = ModalFooter;
